/**
 * CurrencyInput - Componente de input de moeda com máscara
 * Aplica formatação de moeda automaticamente enquanto usuário digita
 * Usa idioma do i18n para determinar formato
 * 
 * Versão: 1.0.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { 
  formatCurrency, 
  parseCurrencyValue, 
  getCurrencyConfig,
  getCurrencyPlaceholder 
} from '@/lib/currency-utils';
import { cn } from '@/lib/utils';

/**
 * Props do componente CurrencyInput
 */
interface CurrencyInputProps {
  /** Valor numérico em reais (ex: 1234.56) */
  value: number;
  /** Callback chamado quando o valor muda */
  onChange: (value: number) => void;
  /** Código da moeda (sobrescreve padrão do idioma) */
  currency?: string;
  /** Placeholder customizado */
  placeholder?: string;
  /** Desabilita o input */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
  /** ID do elemento */
  id?: string;
  /** Nome do campo */
  name?: string;
}

/**
 * Input de moeda com máscara automática
 * Exibe valor formatado e retorna valor numérico para o formulário
 */
export function CurrencyInput({
  value,
  onChange,
  currency,
  placeholder,
  disabled,
  className,
  id,
  name,
}: CurrencyInputProps) {
  const { i18n } = useTranslation();
  const language = i18n.language || 'pt-BR';
  const config = getCurrencyConfig(language);
  
  // Estado interno para exibição formatada
  const [displayValue, setDisplayValue] = useState<string>('');
  
  // Atualiza display quando valor externo muda
  useEffect(() => {
    if (value === 0 || value === undefined || value === null) {
      setDisplayValue('');
    } else {
      setDisplayValue(formatCurrency(value, { currency: currency || config.currency, language }));
    }
  }, [value, currency, language, config.currency]);
  
  /**
   * Manipula entrada do usuário
   * Aplica máscara enquanto digita
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Remove tudo que não é dígito
    const digits = inputValue.replace(/\D/g, '');
    
    if (digits.length === 0) {
      setDisplayValue('');
      onChange(0);
      return;
    }
    
    // Converte para valor decimal (centavos → reais)
    const numericValue = parseInt(digits, 10) / 100;
    
    // Formata para exibição
    const formatted = formatCurrency(numericValue, { 
      currency: currency || config.currency, 
      language 
    });
    
    setDisplayValue(formatted);
    onChange(numericValue);
  }, [currency, language, config.currency, onChange]);
  
  /**
   * Ao focar, seleciona todo o conteúdo
   */
  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);
  
  return (
    <Input
      id={id}
      name={name}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      placeholder={placeholder || getCurrencyPlaceholder({ currency: currency || config.currency, language })}
      disabled={disabled}
      className={cn('text-right', className)}
    />
  );
}
